import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdChironClickEvent } from '../../hd-chiron.models';
import { PhsCategory, PhsCross, phsCategoryFor } from '../../hd-phs-metadata';
import { HdPhsBlockClickEvent } from '../hd-color-tone-block/hd-color-tone-block.component';
import { HdPlanetActivationBlockClickEvent } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { getHdPlanetDisplayConfig, HD_PLANET_ORDER_STANDARD, HdFixingState, HdPlanetDisplayConfig, HdPlanetKey } from '../hd-planet-display-config';
import { findActivationFromList, resolveHdFixingState } from '../hd-planet-grid.utils';
import { HD_PLANET_GRID_CLASSIC_COLOR_CONFIG, HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { HdSimplePlanetBlockComponent } from '../hd-simple-planet-block/hd-simple-planet-block.component';
import { HdTransformationArrowComponent } from '../hd-transformation-arrow/hd-transformation-arrow.component';

/** Standaard planeetvolgorde van de eenvoudige grid (maan onder de nodes). */
const HD_SIMPLE_PLANET_ORDER: readonly HdPlanetKey[] = HD_PLANET_ORDER_STANDARD;

interface HdSimplePlanetRowVm {
  readonly activation: HdBodygraphPlanetaryActivation;
  readonly displayConfig: HdPlanetDisplayConfig;
  readonly fixingState: HdFixingState;
}

/** Transformatiepijl naast een rijengroep: richting uit de toon van Zon resp. Noordknoop. */
interface HdSimpleArrowVm {
  readonly category: PhsCategory;
  readonly color: number;
  readonly tone: number;
  /** Poorten van de groep: hover op de pijl licht ze op, zoals bij de LPS-grid. */
  readonly gates: readonly number[];
}

/** Opeenvolgende rijen die samen één pijl dragen (Zon + Aarde, Noord- + Zuidknoop). */
interface HdSimpleRowGroupVm {
  readonly key: string;
  readonly rows: readonly HdSimplePlanetRowVm[];
  readonly arrow: HdSimpleArrowVm | null;
}

/** Welke planeten bij welk kruis horen, en welke planeet de toon (pijlrichting) levert. */
const HD_SIMPLE_ARROW_GROUPS: ReadonlyArray<{ readonly cross: PhsCross; readonly keys: readonly HdPlanetKey[]; readonly toneKey: HdPlanetKey }> = [
  { cross: 'incarnation', keys: ['sun', 'earth'], toneKey: 'sun' },
  { cross: 'life', keys: ['northNode', 'southNode'], toneKey: 'northNode' },
];

/**
 * Informatief extra-planeet item (bv. Chiron) dat onderaan de eenvoudige
 * planeetgrid getoond wordt. Geen chart-activatie: geen hover-gedrag, een
 * gedimde regel met tooltip; een klik opent wel de gate-info van haar poort.
 */
export interface HdSimpleExtraPlanetVm {
  readonly glyph: string;
  readonly gate: number;
  readonly line: number;
  readonly tooltip: string;
}

/**
 * Eenvoudige (classic) variant van de planeetgrid: enkel planeetglyph +
 * poort.lijn + exaltatie/detriment-pijlen, in de klassieke verticale volgorde.
 * Deelt het hover/context-menu-contract met `hd-planet-grid` zodat hover-
 * highlight, gate-info-klikken en het freeze-rechtsklikmenu identiek werken.
 * Toont bewust géén lijn-/circuit-statistieken: die rekken de smalle
 * lijstweergave uit (de volledige grid behoudt zijn eigen stats-blok).
 */
@Component({
  selector: 'hd-simple-planet-grid',
  templateUrl: './hd-simple-planet-grid.component.html',
  styleUrl: './hd-simple-planet-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdSimplePlanetBlockComponent, HdTransformationArrowComponent],
  host: {
    '[class.is-hover-active]': 'hoverActive()',
    '(mouseleave)': 'onHostMouseLeave()',
    '(mouseover)': 'onHostMouseOver($event)',
    '(contextmenu)': 'onHostContextMenu($event)',
  },
})
export class HdSimplePlanetGridComponent {
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  /**
   * Toon het eigenlijke planeetgrid-paneel (gebruikt door de zijde-toggle).
   * Default `true` houdt alle bestaande gebruikers ongewijzigd.
   */
  readonly showGrid = input(true);
  /** Optioneel informatief extra-planeet item (Chiron) onderaan de grid. */
  readonly chiron = input<HdSimpleExtraPlanetVm | null>(null);
  /** Shortcut "HD Keynotes": planeet-keynote bij hover/focus op een regel. */
  readonly showHdKeynotes = input(false);
  /**
   * Wanneer uitgeschakeld (globale debug-setting "Rechterklik op poorten")
   * laten we de native browser-contextmenu door en emitten we geen
   * freeze-menu — gelijk aan de gate/center-listeners in de chart.
   */
  readonly contextMenuEnabled = input(true);
  /**
   * De vier transformatiepijlen naast de grid (wens Steven 2026-10-21, zoals bij de LPS-grid):
   * bovenaan ter hoogte van Zon/Aarde, onderaan ter hoogte van de knopen. Uit waar geen ruimte
   * naast de grid is (mini-grids).
   */
  readonly showTransformationArrows = input(true);
  /** Kleur van de pijlen (design/personality), zoals de LPS-grid ze uit de planeetgrid-kleuren haalt. */
  readonly colorConfig = input<HdPlanetGridColorConfig>(HD_PLANET_GRID_CLASSIC_COLOR_CONFIG);

  readonly planetActivationClicked = output<HdPlanetActivationBlockClickEvent>();
  /** Klik op de (gedimde) Chiron-regel: de host opent de gate-info met de Chiron-context. */
  readonly chironClicked = output<HdChironClickEvent>();
  readonly planetBlockContextMenuRequested = output<{ gate: number; position: { x: number; y: number } }>();
  /** Klik op een transformatiepijl: de host opent het PHS-info-paneel (zelfde event als de LPS-grid). */
  readonly phsBlockClicked = output<HdPhsBlockClickEvent>();

  protected readonly sideLabel = computed(() =>
    this.side() === 'personality' ? 'PERSONALITY' : 'DESIGN / BODY',
  );

  protected readonly sideLabelClass = computed(() =>
    this.side() === 'personality' ? 'personality-label' : 'design-label',
  );

  protected readonly rows = computed<readonly HdSimplePlanetRowVm[]>(() => {
    const activations = this.activations();
    const side = this.side();
    const rows: HdSimplePlanetRowVm[] = [];
    for (const key of HD_SIMPLE_PLANET_ORDER) {
      const displayConfig = getHdPlanetDisplayConfig(key);
      const activation = findActivationFromList(activations, side, displayConfig.planetId);
      if (!activation) continue;
      rows.push({ activation, displayConfig, fixingState: resolveHdFixingState(activation) });
    }
    return rows;
  });

  /**
   * De rijen in groepen, in dezelfde volgorde: Zon + Aarde en de twee knopen krijgen elk een
   * pijl die naast de groep hangt (verticaal gecentreerd op die twee rijen), de rest niet.
   */
  protected readonly rowGroups = computed<readonly HdSimpleRowGroupVm[]>(() => {
    const groups: { cross: PhsCross | null; rows: HdSimplePlanetRowVm[] }[] = [];
    for (const row of this.rows()) {
      const cross = HD_SIMPLE_ARROW_GROUPS.find(group => group.keys.includes(row.displayConfig.key))?.cross ?? null;
      const last = groups.at(-1);
      if (last != null && last.cross === cross) last.rows.push(row);
      else groups.push({ cross, rows: [row] });
    }
    return groups.map((group, index) => ({
      key: `${group.cross ?? 'rest'}-${index}`,
      rows: group.rows,
      arrow: group.cross != null ? this.arrowFor(group.cross, group.rows) : null,
    }));
  });

  private arrowFor(cross: PhsCross, rows: readonly HdSimplePlanetRowVm[]): HdSimpleArrowVm | null {
    const side = this.side();
    if (!this.showTransformationArrows() || (side !== 'design' && side !== 'personality')) return null;
    const toneKey = HD_SIMPLE_ARROW_GROUPS.find(group => group.cross === cross)?.toneKey;
    const toneRow = rows.find(row => row.displayConfig.key === toneKey);
    if (toneRow == null) return null;
    return {
      category: phsCategoryFor(side, cross),
      color: toneRow.activation.color,
      tone: toneRow.activation.tone,
      gates: rows.map(row => row.activation.gate),
    };
  }

  protected onArrowClicked(arrow: HdSimpleArrowVm, event: { origin: { x: number; y: number } }): void {
    const side = this.side();
    if (side !== 'design' && side !== 'personality') return;
    this.phsBlockClicked.emit({
      category: arrow.category,
      side,
      color: arrow.color,
      tone: arrow.tone,
      colorConfig: this.colorConfig(),
      origin: event.origin,
    });
  }

  protected readonly hoverActive = computed(() =>
    (this.hoverService?.enabled() ?? false) && (this.hoverService?.isActive() ?? false),
  );

  protected onChironClick(event: MouseEvent): void {
    const side = this.side();
    if (side !== 'design' && side !== 'personality') return;
    this.chironClicked.emit({ side, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onHostMouseLeave(): void {
    this.hoverService?.clearForContainer(['planet-grid-block', 'planet-grid-furnishing']);
  }

  protected onHostMouseOver(event: MouseEvent): void {
    const target = event.target as Element | null;
    // De pijl zet zelf de hover van zijn poorten; niet meteen weer wissen (zoals hd-planet-grid).
    if (target?.closest('hd-simple-planet-block, hd-transformation-arrow')) return;
    this.hoverService?.softClearForContainer(['planet-grid-block', 'planet-grid-furnishing']);
  }

  protected onHostContextMenu(event: MouseEvent): void {
    if (!this.contextMenuEnabled()) return;
    const target = event.target as Element | null;
    const block = target?.closest('hd-simple-planet-block') as HTMLElement | null;
    if (!block) return;
    const gateAttr = block.getAttribute('data-gate');
    const gate = gateAttr != null ? Number.parseInt(gateAttr, 10) : NaN;
    if (!Number.isFinite(gate)) return;
    event.preventDefault();
    event.stopPropagation();
    this.hoverService?.suspendForContextMenu();
    this.planetBlockContextMenuRequested.emit({
      gate,
      position: { x: event.clientX, y: event.clientY },
    });
  }
}
